import { fileDownload } from 'utils/fileDownload';
import { logError } from 'utils/sentryUtils';

import { mmFetch } from './mmFetch';
import { mmFetchWithResponse } from './mmFetchWithResponse';

/**
 * Allowed sort fields for packaging purchase order search.
 * Must match the API's accepted values.
 */
const ALLOWED_SORT_FIELDS = ['id', 'created_at'];

/**
 * Allowed sort directions for packaging purchase order search.
 */
const ALLOWED_SORT_DIRS = ['asc', 'desc'];

/**
 * Builds query params for the V2 packaging purchase order search endpoint.
 * @param {Object} params - Search parameters
 * @returns {URLSearchParams}
 */
const buildPackagingPurchaseOrderSearchParams = (params = {}) => {
    const queryParams = new URLSearchParams();

    // Pagination
    if (params.pageSize !== undefined) queryParams.set('pageSize', params.pageSize);
    if (params.cursor !== undefined) queryParams.set('cursor', params.cursor);
    if (params.totalCount !== undefined) queryParams.set('totalCount', params.totalCount);

    // Text filters
    if (params.poNumber) queryParams.set('poNumber', params.poNumber);
    if (params.referenceNumber) queryParams.set('referenceNumber', params.referenceNumber);

    // Date filters
    if (params.deliveryDateStart) queryParams.set('deliveryDateStart', params.deliveryDateStart);
    if (params.deliveryDateEnd) queryParams.set('deliveryDateEnd', params.deliveryDateEnd);

    // Multi-select array filters
    if (params.vendorIds?.length) {
        params.vendorIds.forEach((id) => queryParams.append('vendorIds[]', id));
    }
    if (params.facilityIds?.length) {
        params.facilityIds.forEach((id) => queryParams.append('facilityIds[]', id));
    }
    if (params.statuses?.length) {
        params.statuses.forEach((s) => queryParams.append('statuses[]', s));
    }

    // Sorting — only send both sortField and sortDir together, and only if valid
    const { sortField } = params;
    const { sortDir } = params;
    if (sortField && sortDir && ALLOWED_SORT_FIELDS.includes(sortField) && ALLOWED_SORT_DIRS.includes(sortDir)) {
        queryParams.set('sortField', sortField);
        queryParams.set('sortDir', sortDir);
    }

    return queryParams;
};

const packagingApi = {
    addItemToPurchaseOrder: async (poID, items) => {
        const url = `api/warehouse/v1/packaging/purchaseOrder/${poID}/items`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify(items),
        });
        return response;
    },
    cancelPurchaseOrder: async (id, reasonCode) => {
        const url = `api/warehouse/v1/packaging/purchaseOrder/${id}/cancel`;
        const requestOptions = {
            method: 'PUT',
        };
        if (reasonCode) {
            requestOptions.body = JSON.stringify({ reasonCode });
        }
        const response = await mmFetchWithResponse(url, requestOptions);
        return response;
    },
    createAutoAssignPackaging: async (item) => {
        const url = `api/warehouse/v1/packaging/auto/config`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify(item),
        });
        return response;
    },
    createPackaging: async (sendParams) => {
        const url = `api/warehouse/v1/packaging`;
        try {
            const response = await mmFetchWithResponse(url, {
                method: 'POST',
                body: JSON.stringify(sendParams),
            });
            return response;
        } catch (e) {
            return false;
        }
    },
    createPurchaseOrder: async (item) => {
        const url = `api/warehouse/v1/packaging/purchaseOrder`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify(item),
        });
        return response;
    },
    getAutoAssignPackaging: async (facilityID) => {
        const url = `api/warehouse/v1/packaging/auto/config?facilityID=${facilityID}`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getAssignments: async () => {
        const url = `api/warehouse/v1/packaging/assignments`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getBoxes: async (companySource) => {
        const url = `api/warehouse/v1/packaging/boxes?companySource=${companySource}`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getInsulation: async (cubeLevelID = 0) => {
        let url = `api/warehouse/v1/packaging/insulation`;
        if (cubeLevelID > 0) {
            url += `?cubeLevelID=${cubeLevelID}`;
        }
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getPackagingTypes: async () => mmFetchWithResponse(`api/warehouse/v1/packaging/types`),
    getPackaging: async () => {
        const url = `api/warehouse/v1/packaging`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getPurchaseOrderDetails: async (poID) => {
        const url = `api/warehouse/v1/packaging/purchaseOrder/${poID}`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getPurchaseOrders: async (status, facilityID) => {
        let url = `api/warehouse/v1/packaging/purchaseOrder?facilityID=${facilityID}`;
        if (status !== '') {
            url += `&status=${status}`;
        }
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getShippingMethods: async () => {
        const url = `api/warehouse/v1/packaging/shippingMethods`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    removeItemFromPO: async (poID, poMapID) => {
        const url = `api/warehouse/v1/packaging/purchaseOrder/${poID}/item/${poMapID}/remove`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
        });
        return response;
    },
    sendPurchaseOrderToVendor: async (poID) => {
        const url = `api/warehouse/v1/packaging/purchaseOrder/${poID}/send`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
        });
        return response;
    },
    updateAutoAssignConfig: async (id, item) => {
        const url = `api/warehouse/v1/packaging/auto/config/${id}`;
        const response = await mmFetchWithResponse(url, {
            method: 'PUT',
            body: JSON.stringify(item),
        });
        return response;
    },
    updatePackaging: async (id, item) => {
        const url = `api/warehouse/v1/packaging/assign/${id}`;
        const response = await mmFetchWithResponse(url, {
            method: 'PUT',
            body: JSON.stringify(item),
        });
        return response;
    },
    updatePurchaseOrder: async (id, item) => {
        const url = `api/warehouse/v1/packaging/purchaseOrder/${id}`;
        const response = await mmFetchWithResponse(url, {
            method: 'PUT',
            body: JSON.stringify(item),
        });
        return response;
    },
    updatePurchaseOrderMap: async (poID, poMapID, updates) => {
        const url = `api/warehouse/v1/packaging/purchaseOrder/${poID}/item/${poMapID}`;
        const response = await mmFetchWithResponse(url, {
            method: 'PUT',
            body: JSON.stringify(updates),
        });
        return response;
    },
    editPackaging: async (id, item) => {
        const url = `api/warehouse/v1/packaging/edit/${id}`;
        const response = await mmFetchWithResponse(url, {
            method: 'PUT',
            body: JSON.stringify(item),
        });
        return response;
    },
    downloadPackagingPOPDF: async (poID, vendorName) => {
        const filename = `PO#${poID}-${vendorName}.pdf`;
        const url = `api/warehouse/v1/packaging/purchaseOrder/${poID}/pdf`;
        try {
            const res = await mmFetch(url);
            if (res.ok === true) {
                await fileDownload(res, filename);
                return { status: true };
            }
            let errorMessage = 'Failed to download PDF.';
            try {
                const payload = await res.json();
                if (payload?.msg) {
                    errorMessage = payload.msg;
                }
            } catch (err) {
                // Ignore JSON parsing failures.
            }
            return { status: false, msg: errorMessage };
        } catch (e) {
            logError(e);
            return { status: false, msg: 'Failed to download PDF.' };
        }
    },
    downloadPackagingPOShipList: async (poID) => {
        const filename = `packaging_ship_list_${poID}.pdf`;
        const url = `api/warehouse/v1/packaging/purchaseOrder/${poID}/shipList/pdf`;
        try {
            const res = await mmFetch(url);
            if (res.ok === true) {
                await fileDownload(res, filename);
                return { status: true };
            }
            let errorMessage = 'Failed to download Ship-List.';
            try {
                const payload = await res.json();
                if (payload?.msg) {
                    errorMessage = payload.msg;
                }
            } catch (err) {
                // Ignore JSON parsing failures.
            }
            return { status: false, msg: errorMessage };
        } catch (e) {
            logError(e);
            return { status: false, msg: 'Failed to download Ship-List.' };
        }
    },

    /**
     * Search packaging purchase orders with cursor-based pagination (V2).
     * @param {Object} params - Search parameters including filters and pagination
     * @param {Object} [options] - Fetch options (e.g., { signal } for AbortController)
     * @returns {Promise<{status: boolean, msg: string, data: {rows: Array, pagination: Object, deptLeadCheck?: boolean}}>}
     */
    searchPackagingPurchaseOrdersV2: async (params = {}, options = {}) => {
        const queryParams = buildPackagingPurchaseOrderSearchParams(params);
        const url = `api/warehouse/v2/packaging/purchaseOrders?${queryParams}`;
        return mmFetchWithResponse(url, options);
    },
    /**
     * Fetch packaging purchase order entity (primary + items) by ID (V2).
     * @param {string|number} purchaseOrderId
     * @returns {Promise<{status: boolean, msg?: string, data?: Object}>}
     */
    getPurchaseOrderEntityV2: async (purchaseOrderId) => {
        const url = `api/warehouse/v2/packaging/purchaseOrders/${purchaseOrderId}`;
        return mmFetchWithResponse(url);
    },
};

export { packagingApi };
